/**
 * ProductTempProfileCard — the mix design's Temperature Profile graph.
 *
 * Plots the temperature *gain* curves of example pour locations that used
 * this mix. Each source series is re-based per the legacy report formula
 * (cumulative sum of per-reading deltas, which telescopes to
 * temp − first reading) and aligned to hours elapsed since its first
 * reading, so pours placed on different dates and at different placement
 * temperatures overlay as comparable hydration curves. "Edit Profile"
 * opens the picker (optional chart title plus up to four Select Pour / Select
 * Location rows restricted to pours of this mix); raw data comes from
 * the existing per-location chart endpoint.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Close as ClearIcon,
} from '@mui/icons-material';
import Highcharts from 'highcharts/highstock';
import HighchartsReact from 'highcharts-react-official';
import productsService, {
  type TempProfileResponse,
  type TempProfileSourceItem,
} from '../services/products';
import { poursAPI, type Pour, type PourLocation2 } from '../services/pours';
import { SectionActions } from './product/SectionCard';

// Series colour cycle — matches the dashboard multi-location chart.
const SERIES_COLORS = ['#1976d2', '#d32f2f', '#388e3c', '#f57c00'];

const MAX_SOURCES = 4;

interface SourceDraft {
  pour: Pour | null;
  location: PourLocation2 | null;
}

interface SeriesData {
  name: string;
  data: number[][];
}

const sourceLabel = (s: { pour_code?: string | null; pour_name?: string | null; location_description?: string | null }, index: number): string => {
  const pourPart = s.pour_code || s.pour_name || `Pour ${index + 1}`;
  return s.location_description ? `${pourPart} - ${s.location_description}` : pourPart;
};

/** The chart shows the first week after placing. */
const PROFILE_HOURS = 168;

const locationLabel = (l: PourLocation2): string =>
  l.LocationDescription ||
  (l.LoggerCode ? `${l.LoggerCode} ch${l.ChannelNo ?? ''}` : '') ||
  `Location ${(l.LocationID || l.ID || '').slice(0, 8)}`;

interface ProductTempProfileCardProps {
  productId: string;
  canManage: boolean;
  onError: (msg: string) => void;
  onSuccess: (msg: string) => void;
}

const ProductTempProfileCard: React.FC<ProductTempProfileCardProps> = ({
  productId,
  canManage,
  onError,
  onSuccess,
}) => {
  const [profile, setProfile] = useState<TempProfileResponse | null>(null);
  const [series, setSeries] = useState<SeriesData[]>([]);
  const [loading, setLoading] = useState(true);

  // Edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [identification, setIdentification] = useState('');
  const [drafts, setDrafts] = useState<SourceDraft[]>([]);
  const [mixPours, setMixPours] = useState<Pour[]>([]);
  const [poursLoading, setPoursLoading] = useState(false);
  const [locationsByPour, setLocationsByPour] = useState<Record<string, PourLocation2[]>>({});

  const loadSeries = useCallback(async (sources: TempProfileSourceItem[]) => {
    const results = await Promise.allSettled(
      sources.map((s) => poursAPI.getLocationChartData(s.pour_id, s.location_id))
    );
    const next: SeriesData[] = [];
    results.forEach((r, i) => {
      if (r.status === 'fulfilled' && Array.isArray(r.value?.temp) && r.value.temp.length > 0) {
        // Legacy "Profile" formula: cumulative gains telescope to
        // temp − first reading; x is hours since the first reading so
        // sources from different pours overlay at t=0.
        const [t0Ms, baseTemp] = r.value.temp[0];
        // Only the first days matter: the heat of hydration peaks and settles
        // well inside a week, and a pour logged for months would squash it.
        const data = r.value.temp
          .map(([ms, t]) => [(ms - t0Ms) / 3_600_000, t - baseTemp])
          .filter(([h]) => h <= PROFILE_HOURS);
        next.push({ name: sourceLabel(sources[i], i), data });
      }
    });
    setSeries(next);
  }, []);

  const loadProfile = useCallback(async () => {
    setLoading(true);
    try {
      const data = await productsService.getProductTempProfile(productId);
      setProfile(data);
      await loadSeries(data.sources);
    } catch (err: any) {
      onError(err.response?.data?.detail || 'Failed to load temperature profile');
    } finally {
      setLoading(false);
    }
  }, [productId, loadSeries]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const ensureLocations = async (pourId: string) => {
    if (locationsByPour[pourId]) return;
    try {
      const locations = await poursAPI.getPourLocations2(pourId);
      setLocationsByPour((prev) => ({ ...prev, [pourId]: locations }));
      // Seeded drafts hold stub location objects (from the saved profile);
      // swap them for the fetched rows so labels resolve properly.
      setDrafts((prev) =>
        prev.map((d) =>
          d.pour?.PourID === pourId && d.location
            ? { ...d, location: locations.find((l) => l.ID === d.location!.ID) || d.location }
            : d
        )
      );
    } catch {
      setLocationsByPour((prev) => ({ ...prev, [pourId]: [] }));
    }
  };

  const handleOpenEdit = async () => {
    // Seed the drafts from the saved profile
    setIdentification(profile?.identification || '');
    const seeded: SourceDraft[] = (profile?.sources || []).map((s) => ({
      pour: { PourID: s.pour_id, PourCode: s.pour_code || undefined, PourName: s.pour_name || undefined } as Pour,
      location: { ID: s.location_id, LocationDescription: s.location_description || undefined } as PourLocation2,
    }));
    while (seeded.length < MAX_SOURCES) seeded.push({ pour: null, location: null });
    setDrafts(seeded);
    setEditOpen(true);

    setPoursLoading(true);
    try {
      // Only this mix's pours, filtered by the server (the full list can be
      // tens of thousands of pours).
      const pours = await poursAPI.getAllPourPages({ status: 'All', productId });
      // The profile's own pours (chosen or picked automatically) are always
      // options, even when the general pour list leaves older ones out.
      const known = new Set(pours.map((p) => p.PourID));
      const fromProfile = (profile?.sources || [])
        .filter((s) => !known.has(s.pour_id))
        .map((s) => ({ PourID: s.pour_id, PourCode: s.pour_code || undefined, PourName: s.pour_name || undefined } as Pour));
      setMixPours([...fromProfile, ...pours]);
      // Preload location lists for pours already selected
      await Promise.all((profile?.sources || []).map((s) => ensureLocations(s.pour_id)));
    } catch (err: any) {
      onError(err.response?.data?.detail || 'Failed to load pours for this mix');
    } finally {
      setPoursLoading(false);
    }
  };

  const setDraft = (index: number, patch: Partial<SourceDraft>) => {
    setDrafts((prev) => prev.map((d, i) => (i === index ? { ...d, ...patch } : d)));
  };

  const handleSave = async () => {
    const seen = new Set<string>();
    const sources = drafts
      .filter((d) => d.pour && d.location)
      .map((d) => ({ pour_id: d.pour!.PourID, location_id: d.location!.ID }))
      .filter((s) => {
        const key = `${s.pour_id}|${s.location_id}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    setSaving(true);
    try {
      const updated = await productsService.updateProductTempProfile(productId, {
        identification: identification.trim() || null,
        sources,
      });
      setProfile(updated);
      await loadSeries(updated.sources);
      setEditOpen(false);
      onSuccess('Temperature profile updated');
    } catch (err: any) {
      onError(err.response?.data?.detail || 'Failed to save temperature profile');
    } finally {
      setSaving(false);
    }
  };

  // Plain (non-stock) chart: x is elapsed hours since each source's first
  // reading, y is temperature gain over that first reading — so different
  // pours overlay as comparable curves regardless of placement date/temp.
  const chartOptions: Highcharts.Options = {
    chart: {
      height: 300,
      style: { fontFamily: 'inherit' },
      zooming: { type: 'x' },
      panning: { enabled: true, type: 'x' },
      panKey: 'shift',
    },
    title: {
      text: profile?.identification || '',
      align: 'left',
      style: { fontSize: '13px', fontWeight: '600' },
    },
    xAxis: {
      title: { text: 'Elapsed time (hours)' },
      min: 0,
      crosshair: true,
    },
    yAxis: {
      // "Profile" matches the legacy export's column name for this value
      // (cumulative temperature gain since the first reading).
      title: { text: 'Rise since placing (°C)' },
      opposite: false,
    },
    tooltip: {
      headerFormat: '<span style="font-size: 10px">{point.key:.1f} h after logging start</span><br/>',
      valueDecimals: 1,
      valueSuffix: ' °C',
    },
    legend: { enabled: series.length > 1 },
    credits: { enabled: false },
    series: series.map((s, i) => ({
      type: 'spline',
      name: s.name,
      data: s.data,
      color: SERIES_COLORS[i % SERIES_COLORS.length],
      lineWidth: 2,
      marker: { enabled: false },
    })) as Highcharts.SeriesOptionsType[],
  };

  const hasSources = (profile?.sources.length ?? 0) > 0;

  const automatic = !!profile?.automatic;

  return (
    <Box>
      {/* What the chart shows is the host card's subtitle; the button goes in its header */}
      {canManage && (
        <SectionActions>
          <Button
            size="small"
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={handleOpenEdit}
            sx={{ textTransform: 'none' }}
          >
            {automatic ? 'Choose pours' : 'Edit pours'}
          </Button>
        </SectionActions>
      )}
      {automatic && hasSources && (
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 1 }}>
          Showing the most recent pours of this mix. Choose pours to pick your own.
        </Typography>
      )}
      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress size={28} />
        </Box>
      ) : hasSources && series.length > 0 ? (
        <HighchartsReact highcharts={Highcharts} options={chartOptions} />
      ) : hasSources ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          No logged temperature data found for the selected pour locations yet.
        </Typography>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
          No pours of this mix have logged temperatures yet. The chart fills in once one does.
        </Typography>
      )}

      {/* Edit Profile — Identification + up to four pour/location sources */}
      <Dialog open={editOpen} onClose={() => !saving && setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Choose pours for the temperature profile</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {profile?.automatic && (profile?.sources.length ?? 0) > 0 && (
              <Typography variant="body2" color="text.secondary">
                These are the most recent pours of this mix, picked automatically. Change any of them and save to
                keep your choice. Clear every row and save to go back to automatic.
              </Typography>
            )}
            <TextField
              label="Chart title (optional)"
              size="small"
              value={identification}
              onChange={(e) => setIdentification(e.target.value)}
              inputProps={{ maxLength: 100 }}
              placeholder="For example: Summer 2026 slab pours"
              InputLabelProps={{ shrink: true }}
            />
            <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                Pour
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                Sensor
              </Typography>
              <Box sx={{ width: 34 }} />
            </Box>
            {drafts.map((draft, i) => {
              const locations = draft.pour ? locationsByPour[draft.pour.PourID] || [] : [];
              return (
                <Box key={i} sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Autocomplete
                    options={mixPours}
                    loading={poursLoading}
                    getOptionLabel={(p) => `${p.PourCode || ''}${p.PourName ? ` - ${p.PourName}` : ''}` || p.PourID}
                    isOptionEqualToValue={(o, v) => o.PourID === v.PourID}
                    value={draft.pour}
                    onChange={(_e, pour) => {
                      setDraft(i, { pour, location: null });
                      if (pour) ensureLocations(pour.PourID);
                    }}
                    size="small"
                    sx={{ flex: 1 }}
                    renderInput={(params) => <TextField {...params} placeholder="Pour" />}
                  />
                  <Autocomplete
                    options={locations}
                    getOptionLabel={locationLabel}
                    isOptionEqualToValue={(o, v) => o.ID === v.ID}
                    value={draft.location}
                    onChange={(_e, location) => setDraft(i, { location })}
                    disabled={!draft.pour}
                    noOptionsText={draft.pour ? 'No locations on this pour' : 'Select a pour first'}
                    size="small"
                    sx={{ flex: 1 }}
                    renderInput={(params) => <TextField {...params} placeholder="Sensor" />}
                  />
                  <Tooltip title="Clear row">
                    <span>
                      <IconButton
                        size="small"
                        disabled={!draft.pour && !draft.location}
                        onClick={() => setDraft(i, { pour: null, location: null })}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              );
            })}
            {mixPours.length === 0 && !poursLoading && (
              <Typography variant="caption" color="text.secondary">
                No pours have used this mix design yet.
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProductTempProfileCard;
